import { useMemo } from "react";
import { useAuth } from "./useAuth";

/**
 * 權限檢查 Hook
 * 提供基於用戶角色的權限檢查功能
 */
export function usePermissions() {
  const { user } = useAuth();

  const permissions = useMemo(() => {
    const isAdmin = user?.isAdmin ?? false;
    const isStaff = user?.isStaff ?? false;
    const isDeveloper = user?.isDeveloper ?? false;
    const isObserver = user?.isObserver ?? false;
    
    // developer 視為有 staff 權限
    const hasStaffPermissions = isStaff || isDeveloper;

    return {
      // 基本權限
      isAdmin,
      isStaff,
      isDeveloper,
      isObserver,
      isAuthenticated: !!user,

      // 讀取權限
      canRead: (collection: string): boolean => {
        // 管理員、工作人員、developer 和 observer 可以讀取所有集合
        if (hasStaffPermissions || isObserver) return true;
        
        // 一般用戶只能讀取公開集合
        const publicCollections = [
          "dramas",
          "videos",
          "tags",
          "announcements",
          "actors",
          "companies",
          "products",
        ];
        return publicCollections.includes(collection);
      },

      // 寫入權限
      canWrite: (collection: string): boolean => {
        // 只有管理員、工作人員和 developer 可以寫入
        return hasStaffPermissions;
      },

      // 刪除權限
      canDelete: (collection: string): boolean => {
        // 只有管理員可以刪除
        return isAdmin;
      },

      // 特定集合的權限檢查
      canManageUsers: hasStaffPermissions,
      canManageDramas: hasStaffPermissions,
      canManageVideos: hasStaffPermissions,
      canManageAnnouncements: hasStaffPermissions,
      canManageComments: hasStaffPermissions,
      canManageTransactions: hasStaffPermissions,
      canViewAnalytics: hasStaffPermissions,
      canManageSettings: isAdmin,
      canManageIAM: isAdmin, // 只有管理員可以管理 IAM
    };
  }, [user]);

  return permissions;
}

